import React from "react";
import {
  ReceiptText,
  CheckCircle2,
  ArrowRight,
  FileText,
  CreditCard,
  BellRing,
  Calculator,
  History,
  ShieldCheck,
  BarChart3,
  Globe,
  Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function BillingInvoicingPage() {
  const features = [
    {
      icon: FileText,
      title: "Pro-forma & GST Invoicing",
      description:
        "Generate professional, GST-compliant invoices and pro-forma documents with automated tax calculations.",
    },
    {
      icon: Wallet,
      title: "Subscription Billing",
      description:
        "Manage recurring revenue cycles with automated monthly, quarterly, or annual billing schedules.",
    },
    {
      icon: BellRing,
      title: "Automated Reminders",
      description:
        "Reduce DSOs with smart payment reminders via SMS, WhatsApp, and Email for overdue accounts.",
    },
    {
      icon: History,
      title: "Ledger Management",
      description:
        "Real-time client ledgers with complete transaction history, credits, and adjustment tracking.",
    },
    {
      icon: Calculator,
      title: "Multi-Currency Engine",
      description:
        "Invoice global clients with real-time exchange rate conversion and localized tax compliance.",
    },
    {
      icon: BarChart3,
      title: "Revenue Analytics",
      description:
        "Deep insights into aging reports, collection efficiency, and projected cash flow velocity.",
    },
  ];

  return (
    <main className="bg-white selection:bg-blue-100 font-inter">
      {/* ========== HERO SECTION ========== */}
      <section className="relative h-[450px] flex items-center overflow-hidden bg-[#0a192f]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25 scale-105"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=1600')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f] via-[#0a192f]/90 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 w-full text-left">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold tracking-[0.2em] uppercase mb-4">
              Financial Ecosystem
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight">
              Smart <span className="text-blue-500">Invoicing</span> Solutions.
            </h1>
            <p className="text-base text-slate-400 text-white max-w-lg mb-8 leading-relaxed font-light">
              Accelerate your cash flow with an automated billing system
              designed for precision, compliance, and enterprise-grade security.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link
                to="/contact"
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-900/40 flex items-center gap-2"
              >
                Initiate Free Demo <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CORE VALUE PROPOSITION ========== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center text-left">
            <div className="lg:col-span-5">
              <span className="text-blue-600 text-[11px] font-bold tracking-[0.2em] uppercase block mb-4">
                Fiscal Precision
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
                Automate your <br />
                <span className="text-slate-400">Receivables.</span>
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed text-sm">
                Manual billing leads to errors and delayed payments. Our
                platform orchestrates your entire invoicing process, from quote
                generation to final payment reconciliation.
              </p>
              <div className="space-y-4">
                {[
                  {
                    t: "GST-Ready Compliance",
                    d: "Automatically generate HSN/SAC codes and GST-ready invoices.",
                  },
                  {
                    t: "Payment Gateway Sync",
                    d: "Directly integrate with UPI, Razorpay, and Bank Transfers.",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100"
                  >
                    <CheckCircle2
                      className="text-blue-600 shrink-0"
                      size={20}
                    />
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">
                        {item.t}
                      </h4>
                      <p className="text-xs text-slate-500 mt-1">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 relative">
              <div className="absolute -inset-4 bg-blue-100/30 rounded-[2rem] -z-10 blur-xl" />

              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
                alt="Billing Dashboard"
                className="rounded-2xl shadow-2xl border border-slate-100"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ========== OPERATIONAL IMPACT CARDS ========== */}
      <section className="py-24 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 text-left">
            <span className="text-blue-600 text-[11px] font-bold tracking-[0.2em] uppercase block mb-3">
              Revenue Velocity
            </span>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
              Financial Control Reimagined
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-slate-200 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group text-left">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <ReceiptText size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4 tracking-tight group-hover:text-blue-600 transition-colors">
                Instant Invoicing
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed font-light">
                Create and dispatch legally compliant invoices in under 60
                seconds with pre-built professional templates.
              </p>
            </div>

            <div className="bg-white border border-slate-200 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group text-left">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <CreditCard size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4 tracking-tight group-hover:text-blue-600 transition-colors">
                Multiple Channels
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed font-light">
                Give your customers the flexibility to pay via UPI, Cards, Net
                Banking, or offline methods with auto-matching.
              </p>
            </div>

            <div className="bg-white border border-slate-200 p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group text-left">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4 tracking-tight group-hover:text-blue-600 transition-colors">
                Audit Readiness
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed font-light">
                Every transaction is logged with an immutable audit trail,
                making GST filing and audits completely stress-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY CAPABILITIES CARDS ========== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 text-left">
            <span className="text-blue-600 text-[11px] font-bold tracking-[0.2em] uppercase block mb-3">
              Enterprise Stack
            </span>
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
              Billing Domain Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="group bg-white border border-slate-100 p-8 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-500/30 transition-all duration-300 text-left relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/50 rounded-full -mr-12 -mt-12 group-hover:bg-blue-100 transition-colors" />

                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-50 text-slate-900 mb-6 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm relative z-10">
                  <f.icon size={20} />
                </div>

                <h3 className="text-[17px] font-bold text-slate-900 mb-3 tracking-tight group-hover:text-blue-600 transition-colors relative z-10">
                  {f.title}
                </h3>

                <p className="text-[13px] text-slate-500 leading-relaxed font-normal relative z-10">
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 bg-[#0a192f] relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-10 pointer-events-none text-left">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-blue-500 rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-6 tracking-tight">
            Scale your financial velocity.
          </h2>
          <p className="text-slate-400 mb-8 font-light max-w-xl mx-auto leading-relaxed">
            Stop chasing payments manually. Deploy an automated billing
            infrastructure that works while you sleep.
          </p>
          <div className="flex justify-center">
            <Link href="/contact">
              <span className="bg-white text-slate-900 px-8 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-50 transition-all shadow-xl inline-block cursor-pointer">
                Initiate Technical Demo
              </span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
